<template>
  <app-modal
    v-bind="$attrs"
    ref="modal"
    class="modal-container"
    content-class="modal-medium"
  >
    <template v-slot:header="{ cancel }">
      <v-card-title class="screen-title">IPsec回線設定</v-card-title>
      <template class="d-flex flex-wrap ga-2 justify-end">
        <v-btn color="primary" @click="downloadConfig">設定ダウンロード </v-btn>
        <v-btn color="primary" @click="uploadConfig">設定アップロード </v-btn>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click="cancel()"
        >
          ×
        </button>
      </template>
    </template>

    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{
          lanIpAddressList: form.lanIpAddressList,
        }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 変更時のみ表示 -->
                <div v-if="isEdit">
                  <!-- IPsecGW回線番号 -->
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="IPsecGW回線番号"
                        :value="form.enumber"
                      />
                    </v-col>
                  </v-row>
                  <!-- IPsec回線番号 -->
                  <v-row>
                    <v-col class="mt-2">
                      <app-label-group
                        label="IPsec回線番号"
                        :value="ipSecLine!.xnumber"
                      />
                    </v-col>
                  </v-row>
                </div>
                <!-- 追加時のみ表示 -->
                <!-- IPsecGW回線番号 -->
                <v-row v-else>
                  <v-col>
                    <app-badge
                      item="IPsecGW回線番号"
                      :isInfo="false"
                    ></app-badge>
                    <validation-provider
                      label="IPsecGW回線番号"
                      name="enumber"
                      v-model="form.gw"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select-search
                        :items="selectableGwList"
                        item-title="enumber"
                        return-object
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      ></app-select-search>
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- IPsec回線名 -->
                <v-row>
                  <v-col class="mt-2">
                    <app-badge item="IPsec回線名" msg="20文字以内"></app-badge>
                    <validation-provider
                      label="IPsec回線名"
                      name="ipsecLineName"
                      :rules="{ required: true, max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                      v-model="form.ipsecLineName"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- USER-FQDN -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="USER-FQDN"
                      :isInfo="!isEdit"
                      msg="半角英数、記号(-+._)を含む32文字以内"
                      :isRequired="!isEdit"
                    ></app-badge>
                    <div v-if="isEdit">
                      <span class="content">
                        {{ `${form.userAccount}@${form.gw.suffix}` }}
                      </span>
                    </div>
                    <v-row v-else>
                      <v-col cols="6">
                        <validation-provider
                          label="USER-FQDN"
                          name="userAccount"
                          v-model="form.userAccount"
                          :rules="{
                            required: true,
                            max: { length: 32 },
                            is_half_width_alpha_numeric_symbol: true,
                          }"
                          v-slot="{ componentField, errorMessage, meta }"
                        >
                          <app-form-input
                            v-bind="componentField"
                            :error-messages="errorMessage"
                            :field-meta="meta"
                          />
                        </validation-provider>
                      </v-col>
                      <v-col class="pa-3">
                        @{{ form.gw ? form.gw.suffix : "" }}
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <!-- 事前共有鍵 -->
                <v-row>
                  <v-col class="mt-2">
                    <app-badge
                      item="事前共有鍵"
                      msg="半角英数、記号(-+._)を含む8文字以上
                          64文字以内"
                    ></app-badge>
                    <validation-provider
                      label="事前共有鍵"
                      name="preSharedKey"
                      :rules="{
                        required: true,
                        between_length: { min: 8, max: 64 },
                        is_half_width_alpha_numeric_symbol: true,
                      }"
                      v-model="form.preSharedKey"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 疎通試験用IPアドレス -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="疎通試験用IPアドレス"
                      msg="IPアドレス形式(例:192.168.0.1)"
                    ></app-badge>
                    <validation-provider
                      label="疎通試験用IPアドレス"
                      name="connectTestIpAddress"
                      :rules="{
                        required: true,
                        is_ipv4: true,
                      }"
                      v-model="form.connectTestIpAddress"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :placeholder="
                          $filter.message('placeholder_ip_address_2')
                        "
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- LANアドレス -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="LANアドレス"
                      :msg="
                        $filter.message(
                          `info_cidr_ip_address_of_network_address`,
                          {
                            ipaddress1: '192.168.0.0/24',
                            genre: 'LANアドレス',
                            subnet1: '1',
                            subnet2: '30',
                            ipaddress2: '192.168.0.1/32',
                          }
                        )
                      "
                      :isRequired="form.lanIpAddressList.length > 0"
                    ></app-badge>
                    <validation-array
                      name="lanIpAddressList"
                      v-slot="{ fields, push, remove }"
                      ref="lanIpAddressList"
                    >
                      <template v-for="(e, index) in fields" :key="e.key">
                        <v-row class="ga-2">
                          <validation-provider
                            label="LANアドレス"
                            :name="`lan_${e.key}`"
                            :rules="{
                              required: form.lanIpAddressList.length !== 0,
                              is_ip_subnet: { min: 1, max: 32, exclude: [31] },
                              is_not_current_ip: true,
                              is_not_loopback_ip: true,
                              is_not_multicast_ip: true,
                              is_not_class_e_ip: true,
                              is_not_broadcast_ip: true,
                              is_network_address: true,
                              duplicate: { list: lanAddressList(index) },
                            }"
                            v-model="form.lanIpAddressList[index].ipAddress"
                            v-slot="{ componentField, errorMessage, meta }"
                          >
                            <app-form-input
                              :disabled="
                                form.lanIpAddressList[index]
                                  .acLineIpAddressSeq !== null
                              "
                              :placeholder="
                                form.lanIpAddressList[index]
                                  .acLineIpAddressSeq !== null
                                  ? null
                                  : $filter.message(
                                      'placeholder_ip_address_of_private_network_address'
                                    )
                              "
                              v-bind="componentField"
                              :error-messages="errorMessage"
                              :field-meta="meta"
                            />
                            <v-btn
                              icon="$mdiMinus"
                              size="small"
                              variant="outlined"
                              class="rounded-sm"
                              @click="removeLAN(index, remove)"
                            />
                          </validation-provider>
                        </v-row>
                      </template>
                      <v-row justify="end">
                        <v-btn
                          icon="$mdiPlus"
                          size="small"
                          variant="outlined"
                          class="rounded-sm"
                          @click="addLAN(push)"
                        />
                      </v-row>
                    </validation-array>
                  </v-col>
                </v-row>

                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      msg="20文字以内"
                      :isRequired="false"
                    ></app-badge>
                    <validation-provider
                      label="備考"
                      name="description"
                      :rules="{ max: { length: 20 } }"
                      v-model="form.description"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>

            <!-- アップロード -->
            <v-alert class="my-4" v-if="!!uploadAlert">{{
              uploadAlert
            }}</v-alert>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  isEdit ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { IpSecGWEntity } from "@/apis/IpSecGwApi";
import { UploadError } from "@/services/ConfigDownloadService";
import type { IpSecLineWithGW } from "@/views/nwSetting/ipSecLine/IpSecLineList.vue";
import cloneDeep from "lodash/cloneDeep";
import IpSecLineViewCD from "@/modals/ipSecLine/IpSecLineViewCD.vue";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "IpSecLineEdit",
  props: {
    /** 変更対象のIPsec回線 + IPsecGW */
    ipSecLine: {
      type: Object as PropType<IpSecLineWithGW>,
      required: false,
    },
    /** IPsec回線排他情報（変更の場合のみ必須） */
    acLineKeyId: {
      type: String as PropType<string>,
      required: false,
    },
  },
  data() {
    const form: IpSecLineWithGW = (() => {
      const form = (cloneDeep(this.ipSecLine) ?? {
        lanIpAddressList: [],
      }) as IpSecLineWithGW;
      // 変更かつアドレスリストが0件の場合、初期化
      if (form.lanIpAddressList.length === 0) {
        form.lanIpAddressList = [];
      }
      //  IPアドレスが空でないものだけ数値順ソート
      else if (form.lanIpAddressList.length > 0) {
        form.lanIpAddressList.sort((a, b) => {
          return this.$service.sortCompare(a, b, [["ipAddress", "asc"]]);
        });
      }
      return form;
    })();
    return {
      uploadAlert: null as string | null,
      form: form,
      gwList: [] as IpSecGWEntity[],
      isLoaded: false,
    };
  },
  computed: {
    /** true: 変更, false: 追加 */
    isEdit(): boolean {
      return !!this.ipSecLine;
    },
    /** 選択可能IPsecGW一覧（サフィックスありかつ、作成上限値に達していない場合のみ表示） */
    selectableGwList(): IpSecGWEntity[] {
      return this.gwList.filter(
        (e) => e.suffix && e.numberOfIpsec < e.upperLimitIpsec
      );
    },
    /** LANアドレスの重複チェック用リスト作成 */
    lanAddressList() {
      return (index: number) => {
        return this.form.lanIpAddressList
          .filter((_, num) => num !== index)
          .map((e) => e.ipAddress);
      };
    },
  },
  async mounted() {
    this.gwList = (await this.$api.ipSecGw.getAll()).ipsecGatewayList;
    this.isLoaded = true;
  },
  methods: {
    // IPsec回線の追加・更新を行う
    async submit() {
      this.uploadAlert = null;
      const note: string | null = await this.$modal.show(IpSecLineViewCD, {
        mode: this.isEdit ? "modify" : "register",
        lines: [
          {
            ...this.form,
            lanIpAddressList: this.form.lanIpAddressList.filter(
              (e) => e.ipAddress
            ),
          },
        ],
      });

      if (this.isEdit) {
        await this.$api.ipSecLine.update(this.ipSecLine!.xnumber, {
          ipsecLineName: this.form.ipsecLineName,
          preSharedKey: this.form.preSharedKey,
          connectTestIpAddress: this.form.connectTestIpAddress,
          description: this.form.description,
          lanIpAddressList: this.form.lanIpAddressList.filter(
            (e) => e.ipAddress
          ),
          acLineKeyId: this.acLineKeyId!,
          note: note,
        });
      } else {
        await this.$api.ipSecLine.post({
          ipsecLineName: this.form.ipsecLineName,
          preSharedKey: this.form.preSharedKey,
          connectTestIpAddress: this.form.connectTestIpAddress,
          description: this.form.description,
          enumber: this.form.gw.enumber,
          userAccount: this.form.userAccount,
          lanIpAddressList: this.form.lanIpAddressList
            .filter((e) => e.ipAddress)
            .map((e) => ({ ipAddress: e.ipAddress })),
          note: note,
        });
      }

      await (
        this.$refs.modal as typeof VueFinalModal & { ok: () => void }
      ).ok();
    },

    // 設定ダウンロード処理
    downloadConfig() {
      if (this.isEdit) {
        this.$service.configDownload.downloadIpsecLine(
          this.$store.state.user.contractSummary.vnumber,
          "modify",
          this.form
        );
      } else {
        this.$service.configDownload.downloadIpsecLine(
          this.$store.state.user.contractSummary.vnumber,
          "add",
          this.form
        );
      }
    },

    // 設定アップロード処理
    async uploadConfig() {
      try {
        const uploadIpsec = this.isEdit
          ? await this.$service.configDownload.uploadIpsecLine(
              this.$store.state.user.contractSummary.vnumber,
              "modify",
              this.ipSecLine!.xnumber
            )
          : await this.$service.configDownload.uploadIpsecLine(
              this.$store.state.user.contractSummary.vnumber,
              "add"
            );
        this.uploadAlert = null;
        await this.$confirm(`IPsec回線設定を上書きします。よろしいですか？`);
        // 追加・変更共通
        this.form.ipsecLineName = uploadIpsec.ipsecLineName;
        this.form.preSharedKey = uploadIpsec.preSharedKey;
        this.form.connectTestIpAddress = uploadIpsec.connectTestIpAddress;
        this.form.description = uploadIpsec.description;
        // 追加のみ
        if (!this.isEdit) {
          (this.form as IpSecLineWithGW | { gw: null }).gw =
            (this.selectableGwList.find(
              (e) => e.enumber === uploadIpsec.enumber
            ) as IpSecLineWithGW["gw"]) ?? null;
          this.form.userAccount = uploadIpsec.userAccount ?? "";
        }
        // LANアドレス一覧は存在しないacLineIpAddressSeqはnullに変更して設定
        const seqSet = new Set(
          this.form.lanIpAddressList
            .filter((e) => e.acLineIpAddressSeq)
            .map((e) => e.acLineIpAddressSeq)
        );
        const uploadIpAddressList = uploadIpsec.lanIpAddressList?.map((e) => {
          if (e.acLineIpAddressSeq) {
            return {
              acLineIpAddressSeq: seqSet.has(e.acLineIpAddressSeq)
                ? e.acLineIpAddressSeq
                : null,
              ipAddress: e.ipAddress,
            };
          } else {
            return {
              acLineIpAddressSeq: null,
              ipAddress: e.ipAddress,
            };
          }
        });
        this.form.lanIpAddressList =
          uploadIpAddressList && uploadIpAddressList.length > 0
            ? uploadIpAddressList
            : [{ acLineIpAddressSeq: null, ipAddress: "" }];
        (
          this.$refs.lanIpAddressList as {
            replace: (items: unknown[]) => void;
          }
        ).replace([...this.form.lanIpAddressList]);
      } catch (e) {
        this.uploadAlert = (e as UploadError).message;
      }
    },

    /** LAN追加 */
    addLAN(push: (item: any) => void) {
      const addItem = { acLineIpAddressSeq: null, ipAddress: "" };
      this.form.lanIpAddressList = [...this.form.lanIpAddressList, addItem];
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },

    /** LAN削除 */
    removeLAN(index: number, remove: (idx: number) => void) {
      this.form.lanIpAddressList.splice(index, 1);
      // ValidationにFieldを削除することを伝える
      remove(index);
    },
  },
});
</script>

<style lang="scss" scoped>
span.content {
  margin-left: 1em;
}
</style>
